import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { PageService, RouterDataService, SePageComponent, ToolbarAction, UploadExternalData } from '@se/platform';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { LoadedDataFromRoute, UploadInfo } from '../../models';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { VnaErrorMatcher } from '../../utils/vna-error-matcher';
import { HelperService } from '../../utils/helper.service';
import { BusinessUnit, OriginPage } from './../../models';
import { OrderBusinessUnitService } from '../../order-business-unit.service';
import {
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelDescription,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { PatientService } from '../dicom/services/patient.service';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { TranslatePipe } from '@ngx-translate/core';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { ImportFileComponent } from '../import-file/import-file.component';
import { FileUploadComponent } from '../uploadcomponent/file-upload.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'toolupload-form',
    templateUrl: './toolupload-form.component.html',
    styleUrls: ['./toolupload-form.component.scss'],
    imports: [
        MatCard,
        NgIf,
        NgClass,
        MatCardTitle,
        ReactiveFormsModule,
        TranslatePipe,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelTitle,
        MatExpansionPanelHeader,
        MatExpansionPanelDescription,
        MatCardContent,
        MatFormField,
        MatInput,
        MatLabel,
        MatDatepickerToggle,
        MatDatepicker,
        MatDatepickerInput,
        MatSelect,
        MatOption,
        NgForOf,
        ImportFileComponent,
        FileUploadComponent
    ],
    standalone: true,
})
export class TooluploadFormComponent extends SePageComponent implements OnInit, OnDestroy {
    @ViewChild(MatAccordion) accordion!: MatAccordion;

    @Input() vnaUploadExternalData?: UploadExternalData;
    @Output() closeModalEvent = new EventEmitter<boolean>(false);

    externalPage: OriginPage;
    filesList: FileList;
    formData: FormGroup = null;
    loadedData: LoadedDataFromRoute;
    matcher: VnaErrorMatcher = new VnaErrorMatcher();
    options: Array<BusinessUnit>;
    showSpinner: boolean = false;
    uploadInfo?: UploadInfo;
    toolbarAction: ToolbarAction | null = null;
    private actionsSubscription!: Subscription;
    private changeThemeSubs: Subscription;
    protected destroyed$ = new Subject<void>();

    constructor(
        pageService: PageService,
        private routerDataService: RouterDataService,
        public datePipe: DatePipe,
        private fb: FormBuilder,
        private helper: HelperService,
        private patientService: PatientService,
        private orderBusiness: OrderBusinessUnitService,
    ) {
        super(pageService);
        this.options = [];
    }

    ngOnInit() {
        this.manageData();
        if (this.externalPage !== OriginPage.EXTERNAL_NOT_DICOM) {
            this.getBusinessOrderList();
        }
    }

    ngOnDestroy() {
        if (this.actionsSubscription) this.actionsSubscription.unsubscribe();
        if (this.changeThemeSubs && !this.changeThemeSubs.closed) {
            this.changeThemeSubs.unsubscribe();
        }
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    showFormValidation(formGroup: string) {
        return this.formData.get(formGroup)?.touched ? true : false;
    }

    showFormError(formGroup: string) {
        return this.formData.get(formGroup)?.invalid ? true : false;
    }

    isDisabledAndNotEmpty(source: string, controlName: string): boolean {
        let nestedFG = this.formData.controls[source] as FormGroup;
        return nestedFG.controls[controlName].disabled && nestedFG.controls[controlName].value;
    }

    onFileUploaded(inputEvent: FileList) {
        this.filesList = inputEvent;
    }

    private manageData(): void {
        if (this.routerDataService && this.routerDataService.data?.from === OriginPage.EXTERNAL_NOT_DICOM) {
            this.externalPage = OriginPage.EXTERNAL_NOT_DICOM;
            let information: UploadInfo = this.routerDataService.data.UploadImagesData[0];
            this.loadedData = this.helper.configurationDataFromThirdPartiesNotDimcom(information);
        } else {
            this.toolbarAction = this.vnaUploadExternalData?.toolbarAction ?? null;
            this.loadedData = this.helper.configurationDataEstensa(this.vnaUploadExternalData!);
        }
        if (!this.loadedData.uploadInfo.Issuer) {
            this.showSpinner = true;
            this.patientService.getPatientInfo(this.loadedData.uploadInfo.PatientId).pipe(takeUntil(this.destroyed$)).subscribe((p) => {
                this.loadedData.uploadInfo.Issuer = p.issuer;
                this.uploadInfo = this.loadedData.uploadInfo;
                this.valorizeForm();
            });
        } else {
            this.uploadInfo = this.loadedData.uploadInfo;
            this.valorizeForm();
        }
    }

    private valorizeForm() {
        this.formData = this.buildForm();
        this.assignBusinessUnitToForm();
        this.showSpinner = false;
    }

    private buildForm(): FormGroup {
        return this.fb.group({
            patientData: this.fb.group({
                issuer: [
                    {
                        value: this.uploadInfo?.Issuer,
                        disabled: true,
                    },
                ],
                patientId: this.formDataBuilderRequired(this.uploadInfo, 'PatientId'),
                surname: this.formDataBuilderRequired(this.uploadInfo, 'Surname'),
                name: this.formDataBuilderRequired(this.uploadInfo, 'Name'),
                sex: this.formDataBuilderRequired(this.uploadInfo, 'Sex'),
                taxCode: this.formDataBuilderOptional(this.uploadInfo, 'TaxCode'),
                birthDate: this.formDataBuilderRequired(this.uploadInfo, 'BirthDate'),
            }),
            orderData: this.fb.group({
                orderDescription: this.formDataBuilderRequired(this.uploadInfo, 'OrderDescription'),
                orderDate: this.formDataBuilderRequired(this.uploadInfo, 'OrderDate'),
                orderProvenance: this.formDataBuilderOptional(this.uploadInfo, 'OrderProvenance'),
                orderNumber: this.formDataBuilderOptional(this.uploadInfo, 'OrderNumber'),
            }),
            physicianData: this.fb.group({
                referringPhysicianName: this.formDataBuilderRequired(this.uploadInfo, 'ReferringPhysicianName'),
                referringPhysicianSurname: this.formDataBuilderRequired(this.uploadInfo, 'ReferringPhysicianSurname'),
            }),
        });
    }

    private assignBusinessUnitToForm() {
        if (this.externalPage !== OriginPage.EXTERNAL_NOT_DICOM) {
            let fgOrderData = this.formData.controls['orderData'] as FormGroup;
            fgOrderData.addControl('orderBusinessUnit', this.businessUnitField());
        }
    }

    private businessUnitField(): FormControl {
        if (this.uploadInfo) {
            return !!this.uploadInfo.OrderBusinessUnit
                ? this.fb.control({ value: this.uploadInfo.OrderBusinessUnit.description, disabled: true })
                : this.formDataBuilderRequired(this.uploadInfo, 'OrderBusinessUnit');
        }
    }

    private formDataBuilderRequired(source: any, fieldName: string): FormControl {
        return this.fb.control(
            {
                value: source[fieldName],
                disabled: this.manageFieldsAbilitation(source, fieldName),
            },
            [Validators.required],
        );
    }

    private formDataBuilderOptional(source: any, fieldName: string): FormControl {
        return this.fb.control({
            value: source[fieldName],
            disabled: this.manageFieldsAbilitation(source, fieldName),
        });
    }

    private manageFieldsAbilitation(source: any, fieldName: string): boolean {
        return !!source[fieldName];
    }

    private getBusinessOrderList() {
        this.orderBusiness.getBusinessUnitsFromAll().subscribe((items) => {
            this.options = [...items];
        });
    }

    closeModal() {
        this.closeModalEvent.emit(true);
    }
}
